import React from 'react';
import { Layout, Button, Dropdown } from 'antd';
import { GlobalOutlined, TeamOutlined, FileTextOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from '../../utils/auth';
import { canManageUsers, canRead } from '../../constants/permissions';
import { useIsMobile } from '../../hooks/useIsMobile';

const { Header, Content, Footer } = Layout;

const MainLayout = ({ children }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const languageItems = [
    {
      key: 'zh-TW',
      label: t('nav.zh'),
      onClick: () => changeLanguage('zh-TW'),
    },
    {
      key: 'en',
      label: t('nav.en'),
      onClick: () => changeLanguage('en'),
    },
    {
      key: 'th',
      label: t('nav.th'),
      onClick: () => changeLanguage('th'),
    },
    {
      key: 'vi',
      label: t('nav.vi'),
      onClick: () => changeLanguage('vi'),
    },
  ];

  const currentLangLabel = {
    'zh-TW': t('nav.zh'),
    en: t('nav.en'),
    th: t('nav.th'),
    vi: t('nav.vi')
  }[i18n.language] || t('nav.zh');

  return (
    <Layout className="min-h-screen overflow-x-hidden">
      <Header className="app-header fixed w-full z-50 bg-wabi-cream-100/95 backdrop-blur-md shadow-md h-14 md:h-16 px-0">
        <div className="flex justify-between items-center max-w-7xl mx-auto px-3 sm:px-4 h-full gap-2 min-w-0">
          <div className="text-base sm:text-xl font-bold text-primary shrink min-w-0 truncate">
            <Link to="/" className="text-primary hover:text-secondary transition-colors">
              {t('nav.brand')}
            </Link>
          </div>

          <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
            {canRead() && (
              <Link to="/reports">
                <Button
                  type="text"
                  icon={<FileTextOutlined />}
                  className="app-header-btn text-primary hover:text-secondary"
                  aria-label={t('nav.reports')}
                >
                  {!isMobile && t('nav.reports')}
                </Button>
              </Link>
            )}

            {canManageUsers() && (
              <Link to="/users">
                <Button
                  type="text"
                  icon={<TeamOutlined />}
                  className="app-header-btn text-primary hover:text-secondary"
                  aria-label={t('nav.users')}
                >
                  {!isMobile && t('nav.users')}
                </Button>
              </Link>
            )}

            <Dropdown
              menu={{ items: languageItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                type="text"
                icon={<GlobalOutlined />}
                className="app-header-btn text-primary hover:text-secondary"
                aria-label={t('nav.language')}
              >
                {!isMobile && currentLangLabel}
              </Button>
            </Dropdown>

            <Button
              type="text"
              onClick={handleLogout}
              className="app-header-btn text-primary hover:text-secondary px-2 sm:px-3"
            >
              {isMobile ? t('nav.logoutShort') : t('nav.logout')}
            </Button>
          </div>
        </div>
      </Header>

      <Content className="pt-14 md:pt-16">
        <div className="min-h-[calc(100vh-56px)] md:min-h-[calc(100vh-64px)]">{children}</div>
      </Content>

      <Footer className="text-center bg-wabi-brown-800 text-wabi-cream-100 py-3 px-4 text-xs sm:text-sm">
        {t('footer.copyright', { year: new Date().getFullYear() })}
      </Footer>
    </Layout>
  );
};

export default MainLayout;
